import type React from "react";
import { ChevronDown, ChevronRight, MessageSquare, Paperclip } from "lucide-react";
import { theme, type as text } from "../../theme";
import { ControlStatusBadge, PendingApprovalChip } from "../controlStatus";

// One control, clause, outcome, criterion or obligation in a standard's list. Same row on every
// standard page, in this order:
//
//   reference (text.ref, the standard's own, never the internal id — lib/controlRef.ts)
//   title     (the control's own name, never repeating the reference)
//   extra     (what only this page has: applicability on the SoA and Legal pages)
//   status    (the one status pill, components/controlStatus.tsx)
//   pending   (awaiting consultant approval)
//   owner     (when the page knows one)
//   evidence  (paperclip + count of attached documents)
//   notes     (speech bubble when the control has notes)
//
// Clicking the row opens the control's detail (ControlDetail) directly beneath it. The row
// never changes anything itself; the one exception is `quickAction`, a control beside the row
// (not inside its button) for a decision people make down a whole list: applicability on the
// Statement of Applicability pages.

/**
 * A reference-only line inside a group (a NIST CSF category, an IWA 48 guidance point): the same
 * reference and title columns as ControlRow, with nothing to open and no status.
 */
export function ReferenceRow({ reference, title, aside }: { reference: string | null; title: string; aside?: React.ReactNode }) {
  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}`, display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 8px 38px", flexWrap: "wrap" }}>
      {reference && <span style={{ ...text.ref, fontWeight: 600, color: theme.color.muted, minWidth: 52, flexShrink: 0 }}>{reference}</span>}
      <span style={{ ...text.body, color: theme.color.heading, flex: "1 1 240px", minWidth: 0 }}>{title}</span>
      {aside}
    </div>
  );
}

export function ControlRow({ reference, title, subtitle, status, novice, pending, owner, evidenceCount, hasNotes, extra, quickAction, open, onToggle, dimmed, children }: {
  reference: string | null;
  title: string;
  /** A second line under the title (the requirement, in guided mode while closed). */
  subtitle?: React.ReactNode;
  /** Upper-case platform status (COMPLIANT / IN_PROGRESS / GAP / NOT_STARTED); null hides the pill. */
  status: string | null;
  novice?: boolean;
  pending?: boolean;
  owner?: string | null;
  evidenceCount?: number;
  hasNotes?: boolean;
  extra?: React.ReactNode;
  /** A control beside the row, outside its button (buttons cannot nest): the SoA applicability toggle. */
  quickAction?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  /** Not applicable / exempt rows read quieter. */
  dimmed?: boolean;
  /** The detail, rendered when open. */
  children?: React.ReactNode;
}) {
  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}`, opacity: dimmed ? 0.75 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", background: open ? theme.color.panelAlt : "transparent" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
          background: open ? theme.color.panelAlt : "transparent", border: "none", cursor: "pointer",
          textAlign: "left", flexWrap: "wrap",
        }}
      >
        <span aria-hidden="true" style={{ display: "flex", color: theme.color.faint, flexShrink: 0 }}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        {reference && (
          <span style={{ ...text.ref, fontWeight: 600, color: theme.color.muted, minWidth: 52, flexShrink: 0 }}>{reference}</span>
        )}
        <span style={{ flex: "1 1 240px", minWidth: 0 }}>
          <span style={{ ...text.body, fontWeight: 600, color: theme.color.heading, display: "block" }}>{title}</span>
          {subtitle && <span style={{ ...text.hint, display: "block", marginTop: 2 }}>{subtitle}</span>}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginLeft: "auto" }}>
          {extra}
          {status && <ControlStatusBadge status={status} novice={novice} />}
          {pending && <PendingApprovalChip />}
          {owner && <span style={{ ...text.meta, whiteSpace: "nowrap" }} title="Owner">{owner}</span>}
          {!!evidenceCount && evidenceCount > 0 && (
            <span title={`${evidenceCount} evidence document${evidenceCount !== 1 ? "s" : ""} attached`} style={{ ...text.meta, display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
              <Paperclip size={12} aria-hidden="true" />{evidenceCount}
            </span>
          )}
          {hasNotes && (
            <span title="Has notes" style={{ display: "inline-flex", color: theme.color.faint }}>
              <MessageSquare size={12} aria-label="Has notes" />
            </span>
          )}
        </span>
      </button>
      {quickAction && <div style={{ flexShrink: 0, padding: "6px 14px 6px 0" }}>{quickAction}</div>}
      </div>
      {open && children && (
        <div style={{ padding: "0 16px 14px 16px", background: theme.color.panel }}>{children}</div>
      )}
    </div>
  );
}
